import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CircularProgress } from "@mui/material";
import { FiX, FiChevronDown, FiChevronRight } from "react-icons/fi";
import { sprintApi } from "../sprintApi";
import { showSuccessToast, showErrorToast } from "../../constants/ToastService.js";
import TitleTooltip from "../Common Components/TitleTooltip";
import "./BulkLiveMoveModal.css";

const pillClass = (s) => (/done|completed|live|production/i.test(s) ? "fd-status-pill--success"
  : /progress|dev|qa|review/i.test(s) ? "fd-status-pill--info"
  : /fail|reject|block/i.test(s) ? "fd-status-pill--danger" : "fd-status-pill--muted");

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const stories = (n) => `${n} ${n === 1 ? "story" : "stories"}`;

// The backend's reasons, as short group headings; unknown ones are shown as sent.
const GROUPS = [
  { key: "pending", test: /already pending/i, label: () => "Request already pending" },
  { key: "approved", test: /already approved/i, label: () => "Request already approved" },
  { key: "status", test: /status|eligible/i, label: (rule) => `Not in ${(rule || "an eligible status").replace(/, | or /g, " / ")} yet` },
  { key: "production", test: /production/i, label: () => "Already in Production" },
];

const groupExcluded = (excluded, rule) => {
  const out = new Map();
  excluded.forEach(t => {
    const g = GROUPS.find(x => x.test.test(t.reason || ""));
    const key = g ? g.key : t.reason || "Other";
    if (!out.has(key)) out.set(key, { key, label: g ? g.label(rule) : key, order: g ? GROUPS.indexOf(g) : GROUPS.length, items: [] });
    out.get(key).items.push(t);
  });
  return [...out.values()].sort((a, b) => a.order - b.order);
};

// Sprint-level "Move to Live": lists the sprint's stories that can be requested
// now (all ticked) and raises their requests in one all-or-nothing call. The
// backend decides what's eligible, so this only shows what it returns.
const BulkLiveMoveModal = ({ sprint, onClose }) => {
  const [data, setData] = useState(null); // { eligible, excluded, eligible_rule }
  const [error, setError] = useState("");
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState("");
  const [blocked, setBlocked] = useState([]); // why the last confirm was refused
  const [openGroups, setOpenGroups] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const abortRef = useRef(null);
  const selectAllRef = useRef(null);

  // keepPicks: after a refused confirm, keep the user's unticks instead of re-ticking everything.
  const load = useCallback(async (keepPicks = false) => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setData(null);
    setError("");
    try {
      const res = await sprintApi.liveMoveCandidates(sprint.id, ctrl.signal);
      if (ctrl.signal.aborted) return;
      if (res.status === "success") {
        const ids = res.data.eligible.map(t => t.id);
        setData(res.data);
        setPicked(prev => (keepPicks ? ids.filter(id => prev.includes(id)) : ids));
      } else {
        setError(res.message || "Could not load the sprint's tickets");
      }
    } catch (e) {
      if (e.name !== "AbortError") setError("Could not load the sprint's tickets");
    }
  }, [sprint.id]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const close = useCallback(() => { if (!submittingRef.current) onClose(); }, [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  const eligible = data?.eligible || [];
  const excluded = data?.excluded || [];
  const allPicked = eligible.length > 0 && picked.length === eligible.length;
  const groups = useMemo(() => groupExcluded(data?.excluded || [], data?.eligible_rule), [data]);
  const toggleGroup = (key) => setOpenGroups(p => (p.includes(key) ? p.filter(k => k !== key) : [...p, key]));

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = picked.length > 0 && !allPicked;
  }, [picked, allPicked]);

  const toggle = (id) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  const toggleAll = () => setPicked(allPicked ? [] : eligible.map(t => t.id));

  const confirm = async () => {
    if (submittingRef.current || !picked.length) return;
    submittingRef.current = true;
    setSubmitting(true);
    setBlocked([]);
    const ids = eligible.filter(t => picked.includes(t.id)).map(t => t.id);
    try {
      const res = await sprintApi.raiseLiveMoveRequests(sprint.id, ids, note.trim() || null);
      if (res.status === "success") {
        showSuccessToast(`${plural(ids.length, "live move request")} raised`);
        submittingRef.current = false;
        onClose();
        return;
      }
      showErrorToast(res.message || "Could not raise the requests");
      if (res.data?.blocked?.length) {
        // Nothing was created; show why and refresh, so the blocked ones drop out of the list.
        setBlocked(res.data.blocked);
        load(true);
      }
    } catch {
      showErrorToast("Could not raise the requests");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="sl2-qc-overlay blm-overlay" onClick={close}>
      <div className="sl2-qc-modal blm-modal" role="dialog" aria-modal="true" aria-labelledby="blm-title" onClick={e => e.stopPropagation()}>
        <div className="sl2-qc-modal__header blm-modal__header">
          <div>
            <h2 id="blm-title">Move to Live · {sprint.sprint_name}</h2>
            <div className="blm-modal__sub">
              {data ? `Stories in an eligible status (${data.eligible_rule}) with no open request.` : "Checking which stories are ready…"}
            </div>
          </div>
          <button type="button" className="sl2-qc-modal__close" onClick={close} aria-label="Close" disabled={submitting}><FiX size={18} /></button>
        </div>

        {data && eligible.length > 0 && (
          <label className="blm-selectall">
            <input type="checkbox" ref={selectAllRef} checked={allPicked} onChange={toggleAll} disabled={submitting} />
            Select all
            <span className="blm-selectall__count">{picked.length} of {eligible.length} selected</span>
          </label>
        )}

        <div className="blm-modal__body">
          {!data && !error && <div className="blm-center"><CircularProgress size={22} /></div>}
          {error && (
            <div className="blm-center blm-error">
              <p>{error}</p>
              <button type="button" className="fd-btn-outline" onClick={() => load()}>Retry</button>
            </div>
          )}
          {data && !eligible.length && (
            <p className="blm-empty">
              {excluded.length ? "None of this sprint's stories can be requested right now." : "This sprint has no stories."}
            </p>
          )}
          {eligible.length > 0 && (
            <ul className="blm-list">
              {eligible.map(t => (
                <li key={t.id}>
                  <label className={`blm-row${picked.includes(t.id) ? " blm-row--on" : ""}`}>
                    <input type="checkbox" checked={picked.includes(t.id)} onChange={() => toggle(t.id)} disabled={submitting} />
                    <span className="blm-row__code">{t.code}</span>
                    <TitleTooltip title={t.title}><span className="blm-row__title">{t.title || "-"}</span></TitleTooltip>
                    <span className={`fd-status-pill blm-row__status ${pillClass(t.status_name || "")}`}>{t.status_name || "-"}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {groups.length > 0 && (
            <section className="blm-excluded" aria-label="Stories not listed">
              <h3 className="blm-excluded__title">Not ready to request · {stories(excluded.length)}</h3>
              {groups.map(g => {
                const open = openGroups.includes(g.key);
                return (
                  <div key={g.key} className="blm-group">
                    <button type="button" className="blm-group__head" aria-expanded={open} onClick={() => toggleGroup(g.key)}>
                      {open ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                      <span className="blm-group__label">{g.label}</span>
                      <span className="blm-group__count">{g.items.length}</span>
                    </button>
                    {open && (
                      <ul className="blm-group__list">
                        {g.items.map(t => (
                          <li key={t.id} className="blm-group__row">
                            <span className="blm-row__code">{t.code}</span>
                            <TitleTooltip title={t.title}><span className="blm-row__title">{t.title || "-"}</span></TitleTooltip>
                            <span className={`fd-status-pill blm-row__status ${pillClass(t.status_name || "")}`}>{t.status_name || "-"}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </section>
          )}
        </div>

        <div className="blm-modal__foot">
          {blocked.length > 0 && (
            <div className="blm-blocked" role="alert">
              <strong>No requests were raised.</strong> These tickets can't be requested:
              <ul>{blocked.map(b => <li key={b.id}><b>{b.code || `#${b.id}`}</b> — {b.reason}</li>)}</ul>
            </div>
          )}
          {data && eligible.length > 0 && (
            <label className="blm-note">
              <span>Note (optional)</span>
              <textarea rows={2} value={note} onChange={e => setNote(e.target.value)} disabled={submitting}
                placeholder="Shared note for every request, e.g. release batch" />
            </label>
          )}
          <div className="blm-actions">
            {data && !eligible.length ? (
              <button type="button" className="fd-btn-outline" onClick={close}>Close</button>
            ) : (
              <>
                <button type="button" className="fd-btn-outline" onClick={close} disabled={submitting}>Cancel</button>
                <button type="button" className="fd-btn-primary" onClick={confirm} disabled={!data || !picked.length || submitting}>
                  {submitting ? "Raising…" : picked.length ? `Raise ${plural(picked.length, "request")}` : "Select stories"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BulkLiveMoveModal;
